import React from 'react'
import { Check, Star, Camera, Clock, Users } from 'lucide-react'

export const PackagesPage: React.FC = () => {
  const packages = [
    {
      name: 'Essential',
      price: '$2,500',
      description: 'Perfect for intimate weddings and elopements',
      features: [
        '4 hours of photography coverage',
        'Online gallery with 200+ edited images',
        'High-resolution digital downloads',
        'Print release included',
        '2-week delivery time'
      ],
      popular: false
    },
    {
      name: 'Classic',
      price: '$4,500',
      description: 'Our most popular package for traditional weddings',
      features: [
        '8 hours of photography coverage',
        'Engagement session included',
        'Online gallery with 500+ edited images',
        'High-resolution digital downloads',
        'Print release included',
        'USB drive with all images',
        '10-day delivery time',
        'Complimentary consultation'
      ],
      popular: true
    },
    {
      name: 'Premium',
      price: '$6,500',
      description: 'Complete wedding photography experience',
      features: [
        '10 hours of photography coverage',
        'Second photographer included',
        'Engagement session included',
        'Online gallery with 800+ edited images',
        'High-resolution digital downloads',
        'Print release included',
        'USB drive with all images',
        '1-week delivery time',
        'Complimentary consultation',
        'Wedding planning timeline assistance'
      ],
      popular: false
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-wide">
            Wedding Photography Packages
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Choose the perfect package for your special day. All packages include our signature fine art editing style and professional service.
          </p>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div key={index} className={`relative bg-white border-2 p-8 ${pkg.popular ? 'border-gray-900' : 'border-gray-200'}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gray-900 text-white px-4 py-2 text-sm font-medium tracking-wide">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-light text-gray-900 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-light text-gray-900 mb-4">{pkg.price}</div>
                  <p className="text-gray-600">{pkg.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <Check className="w-5 h-5 text-gray-900 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button className={`w-full py-3 px-6 font-light tracking-wide transition-colors duration-300 ${
                  pkg.popular 
                    ? 'bg-gray-900 text-white hover:bg-gray-800' 
                    : 'border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white'
                }`}>
                  Select Package
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-light text-gray-900 mb-12 text-center">Popular Add-ons</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { name: 'Additional Hour', price: '$400', icon: Clock },
              { name: 'Second Photographer', price: '$800', icon: Users },
              { name: 'Bridal Session', price: '$600', icon: Camera },
              { name: 'Rehearsal Dinner', price: '$800', icon: Star }
            ].map((addon, index) => {
              const IconComponent = addon.icon
              return (
                <div key={index} className="bg-white p-6 text-center shadow-sm">
                  <IconComponent className="w-8 h-8 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-light text-gray-900 mb-2">{addon.name}</h3>
                  <p className="text-2xl font-light text-gray-900">{addon.price}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}
